/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import Container from '@ba-ui-toolkit/container';
import { classnames as cx, propsUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import styles from './Slideout.scss';

const slideoutClassName = styles.SlideoutClassName.replace(/"/g, '');
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;

export default class Slideout extends Component {
	static defaultProps = {
		active: false,
		toDirection: 'right',
		width: '100%',
		height: '100%',
		resizable: false,
		onHidden: () => {},
		onOpened: () => {},
		onClose: () => {},
		onResizeStart: () => {},
		onResizeStop: () => {},
		onResize: () => {},
		className: '',
		autoFocus: true,
		style: {},
		animationOnRender: false,
	};

	static propTypes = {
		/** Toggles to show the slide in and out effect, by default, slideout pannel is hidden */
		active: PropTypes.bool,
		/** Custom styles passed to the slideout */
		style: PropTypes.object,
		autoFocus: PropTypes.bool,
		/** Slideout direction */
		toDirection: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
		/** Width of the slideout */
		width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Height of the slideout */
		height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Resizability flag, it will be horizontally resizable if turned to true */
		resizable: PropTypes.bool,
		/** Defines resize directions, can be vertical, horizontal or both direction */
		resizeDirection: PropTypes.oneOf(['x', 'y', 'both', 'none']),
		/** Callback function called after panel is hidden */
		onHidden: PropTypes.func,
		onClose: PropTypes.func,
		/** Callback function called after panel is opened */
		onOpened: PropTypes.func,
		/** Callback function called when click away, typical implemention to achieve auto collapse is to set the active prop to false */
		onClickAway: PropTypes.func,
		onResize: PropTypes.func,
		onResizeStart: PropTypes.func,
		onResizeStop: PropTypes.func,
		/** Works together with onClickAway callback. Defines an area where clickaway callback function won't trigger. By default this area is this slideout node itself */
		containerRef: PropTypes.object,
		children: PropTypes.node,
		resizeMinConstraintsX: PropTypes.number,
		resizeMinConstraintsY: PropTypes.number,
		resizeMaxConstraintsX: PropTypes.number,
		resizeMaxConstraintsY: PropTypes.number,
		className: PropTypes.string,
		animationOnRender: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		const { active, animationOnRender } = this.props;
		this.state = {
			showChildren: active,
			hasAnimation: animationOnRender,
		};
	}

	componentDidMount() {
		const { active, animationOnRender } = this.props;
		if (active) {
			this._addListeners();
			if (!animationOnRender) {
				setTimeout(() => {
					// when slideout is opened after mount and no animation on render , adding the clickAway event listener, since in other cases clickAway listener is added when sliding out transition is end,
					// Adding this listener after a cycle is to prevent unexpected closing effect from a clicking event
					this._addClickAwayEventListener();
				});
			}
		}
	}

	componentDidUpdate(prevProps, prevStates) {
		const { active: prevActiveProp } = prevProps;
		const { active: curActiveProp, autoFocus } = this.props;
		const { showChildren: prevShowChildrenState } = prevStates;
		const { showChildren: curShowChildrenState } = this.state;
		if (!prevActiveProp && curActiveProp) {
			this._addListeners();
		} else if (prevShowChildrenState && !curShowChildrenState) {
			this._removeListeners();
		} else if (!prevShowChildrenState && curShowChildrenState && autoFocus) {
			findDOMNode(this).focus();
		}
	}

	componentWillUnmount() {
		this._removeListeners();
	}

	_onTransitionEnd = e => {
		if (e.propertyName === 'transform') {
			// fires the onOpend/onHidden callback when sliding transition is end
			const { active, onOpened, onHidden } = this.props;
			if (active) {
				this._addClickAwayEventListener();
				onOpened(e);
			} else {
				onHidden(e);
			}
			this.setState({ showChildren: active });
		}
	};

	_onAnimationEnd = e => {
		const { active, onOpened } = this.props;
		this._addClickAwayEventListener();
		onOpened(e);
		this.setState({ showChildren: active, hasAnimation: false });
		findDOMNode(this).removeEventListener('animationend', this._onAnimationEnd);
	};

	_addClickAwayEventListener() {
		const { onClickAway } = this.props;
		if (onClickAway) {
			window.addEventListener('click', this._clickAwayEventListener);
		}
	}

	_clickAwayEventListener = e => {
		const { onClickAway, containerRef } = this.props;
		const parent = !containerRef ? this : containerRef;

		if (!findDOMNode(parent).contains(e.target)) {
			onClickAway(e);
		}
	};

	_onKeyDown = e => {
		const { onClose } = this.props;
		if (isKey(e, keyNames.ESCAPE)) {
			onClose(e);
			findDOMNode(this).blur();
		}
	};

	_onResizeStop = (event, data) => {
		const { onClickAway, onResizeStop } = this.props;
		if (onClickAway) {
			// temporarily remove the clickaway listener to prevent the slideout being automatically collapsed if it reaches the max width/height
			window.removeEventListener('click', this._clickAwayEventListener);
			setTimeout(() => {
				window.addEventListener('click', this._clickAwayEventListener);
			});
		}
		onResizeStop(event, data);
	};

	_addListeners() {
		const { hasAnimation } = this.state;
		findDOMNode(this).addEventListener('transitionend', this._onTransitionEnd);
		if (hasAnimation) {
			findDOMNode(this).addEventListener('animationend', this._onAnimationEnd);
		}
	}

	_removeListeners() {
		const { onClickAway } = this.props;
		if (onClickAway) {
			window.removeEventListener('click', this._clickAwayEventListener);
		}
		findDOMNode(this).removeEventListener('transitionend', this._onTransitionEnd);
	}

	render() {
		const {
			width,
			height,
			children,
			style,
			active,
			toDirection,
			resizable,
			resizeDirection,
			resizeMaxConstraintsX,
			resizeMaxConstraintsY,
			resizeMinConstraintsX,
			resizeMinConstraintsY,
			onResizeStart,
			onResize,
			className,
			...others
		} = this.props;
		const { showChildren, hasAnimation } = this.state;
		const slideoutComponentClass = cx(`${slideoutClassName}`, {
			[`${toDirection}`]: toDirection,
			hasAnimation,
			active,
		});

		const sharedProps = {
			...sanitize(others, Slideout),
			tabIndex: -1, // can only be focusable programatically, not through TAB key
			className: `${slideoutComponentClass} ${className}`,
			onKeyDown: this._onKeyDown,
		};

		const slideoutChildren = (
			<div className={`${slideoutClassName}-children`}>
				<div className={`${slideoutClassName}-children-wrapper`} style={{ ...style }}>
					{children}
				</div>
			</div>
		);

		if (resizable) {
			const resizeProps = {
				resizable: true,
				resizeDirection,
				resizeMaxConstraintsX,
				resizeMaxConstraintsY,
				resizeMinConstraintsX,
				resizeMinConstraintsY,
				onResizeStop: this._onResizeStop,
				onResizeStart,
				onResize,
			};

			return (
				<Container {...sharedProps} {...resizeProps} width={width} height={height}>
					{(active || showChildren) && slideoutChildren}
				</Container>
			);
		}

		return (
			<div {...sharedProps} style={{ width, height }}>
				{(active || showChildren) && slideoutChildren}
			</div>
		);
	}
}
